import React, { useState } from 'react'
import SEO from '../components/SEO'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    beskrivelse: '',
    website_url: '', // Antispam honeypot field
  })

  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.navn.trim()) newErrors.navn = 'Angiv venligst dit navn'
    if (!formData.telefon.trim()) {
      newErrors.telefon = 'Angiv venligst dit telefonnummer'
    } else if (!/^[0-9+\s\-()]{8,20}$/.test(formData.telefon.trim())) {
      newErrors.telefon = 'Indtast et gyldigt telefonnummer (mindst 8 cifre)'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Angiv venligst din e-mail'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast en gyldig e-mailadresse'
    }
    if (!formData.beskrivelse.trim()) {
      newErrors.beskrivelse = 'Skriv venligst en kort beskrivelse af din henvendelse'
    }
    return newErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError(null)

    const formErrors = validate()
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url, // Honeypot
        }),
      })

      if (response.ok) {
        setSubmitted(true)
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          beskrivelse: '',
          website_url: '',
        })
      } else {
        const data = await response.json().catch(() => ({}))
        setSubmitError(
          data.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.'
        )
      }
    } catch (err) {
      console.error('Submit error', err)
      setSubmitError('Der opstod en netværksfejl. Kontroller din forbindelse eller ring til os.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <SEO
        title="Kontakt"
        description="Kontakt Birgitte Vilsgaard hos Insightly Care i Svendborg. Ring på 28 83 94 88 eller skriv til bgv@insightly.dk for IT & Tech Recruitment."
      />

      {/* Header Banner */}
      <section className="bg-gradient-to-b from-slate-100 to-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-black text-black tracking-tight">
              Kontakt Insightly Care
            </h1>
            <p className="text-lg text-black mt-3 leading-relaxed">
              Book en uforpliktende samtale om jeres IT-rekrutteringsbehov. Vi vender hurtigt tilbage.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contact Details Card Left */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-slate-900 text-white p-8 space-y-6">
                <div>
                  <h2 className="text-2xl font-black text-white">Birgitte Vilsgaard</h2>
                  <p className="text-slate-300 font-semibold text-sm">
                    Partner & IT Recruitment Specialist
                  </p>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-800 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-800 text-white flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400">Telefon</p>
                      <a
                        href="tel:28839488"
                        className="text-white font-bold text-base hover:text-slate-200 transition-colors whitespace-nowrap"
                      >
                        28 83 94 88
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-800 text-white flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400">E-mail</p>
                      <a
                        href="mailto:bgv@insightly.dk"
                        className="text-white font-bold text-base hover:text-slate-200 transition-colors whitespace-nowrap"
                      >
                        bgv@insightly.dk
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-800 text-white flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400">Adresse</p>
                      <p className="text-white font-semibold">
                        Dronningemaen 68, 5700 Svendborg
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-800 text-white flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-building" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase font-bold text-slate-400">Virksomhedsinfo</p>
                      <p className="text-white font-semibold">Insightly Care (Insightly ApS)</p>
                      <p className="text-xs text-slate-400">CVR: 43779362</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800">
                  <a
                    href="https://www.linkedin.com/in/birgitte-vilsgaard-9b2986b2/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 hover:text-white"
                  >
                    <i className="fa-brands fa-linkedin text-lg text-white" aria-hidden="true" />
                    <span>Forbind med Birgitte på LinkedIn</span>
                  </a>
                </div>
              </div>

              {/* Info note */}
              <div className="p-6 bg-slate-100 border border-slate-200 space-y-2">
                <h3 className="font-bold text-black text-sm flex items-center gap-2">
                  <i className="fa-solid fa-shield-check text-black" aria-hidden="true" />
                  <span>Fortrolighed og Hurtig Respons</span>
                </h3>
                <p className="text-xs text-black leading-relaxed">
                  Alle henvendelser behandles med fuld fortrolighed. Vi tilstræber altid at besvare telefon og beskeder inden for 24 timer på hverdage.
                </p>
              </div>
            </div>

            {/* Form Right */}
            <div className="lg:col-span-7">
              <div className="bg-slate-50/70 p-8 sm:p-10 border border-slate-200">
                <h2 className="text-2xl font-extrabold text-black mb-2">
                  Send en besked
                </h2>
                <p className="text-black text-sm mb-8">
                  Udfyld formularen nedenfor, så vender Birgitte Vilsgaard hurtigt tilbage til dig.
                </p>

                {submitted ? (
                  <div className="p-8 bg-emerald-50 border border-emerald-200 text-emerald-900 text-center space-y-4 animate-in fade-in duration-200">
                    <div className="w-14 h-14 bg-emerald-100 text-emerald-600 flex items-center justify-center text-2xl mx-auto">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h3 className="text-2xl font-bold">Tak for din henvendelse!</h3>
                    <p className="text-sm leading-relaxed max-w-md mx-auto">
                      Din besked er modtaget. Birgitte Vilsgaard vender tilbage til dig hurtigst muligt.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-4 px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs transition-colors cursor-pointer"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    {/* ANTISPAM HONEYPOT FIELD - MANDATORY ABSOLUTELY HIDDEN FROM HUMANS */}
                    <div className="sr-only absolute -left-[9999px] top-0" aria-hidden="true">
                      <label htmlFor="website_url">Bypass URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex="-1"
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={handleChange}
                      />
                    </div>

                    {submitError && (
                      <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-3">
                        <i className="fa-solid fa-triangle-exclamation text-lg shrink-0" aria-hidden="true" />
                        <span>{submitError}</span>
                      </div>
                    )}

                    <div>
                      <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="navn"
                        name="navn"
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className={`w-full px-4 py-3 border bg-white text-slate-900 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                          errors.navn ? 'border-red-400 bg-red-50/30' : 'border-slate-300'
                        }`}
                      />
                      {errors.navn && (
                        <p className="text-red-500 text-xs mt-1 font-medium">{errors.navn}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="telefon"
                          name="telefon"
                          value={formData.telefon}
                          onChange={handleChange}
                          placeholder="F.eks. 28 83 94 88"
                          className={`w-full px-4 py-3 border bg-white text-slate-900 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                            errors.telefon ? 'border-red-400 bg-red-50/30' : 'border-slate-300'
                          }`}
                        />
                        {errors.telefon && (
                          <p className="text-red-500 text-xs mt-1 font-medium">{errors.telefon}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                          E-mailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="din@email.dk"
                          className={`w-full px-4 py-3 border bg-white text-slate-900 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                            errors.email ? 'border-red-400 bg-red-50/30' : 'border-slate-300'
                          }`}
                        />
                        {errors.email && (
                          <p className="text-red-500 text-xs mt-1 font-medium">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                        Beskrivelse <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="beskrivelse"
                        name="beskrivelse"
                        rows={5}
                        value={formData.beskrivelse}
                        onChange={handleChange}
                        placeholder="Beskriv jeres behov for IT-rekruttering, den ønskede profil eller spørgsmål..."
                        className={`w-full px-4 py-3 border bg-white text-slate-900 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 ${
                          errors.beskrivelse ? 'border-red-400 bg-red-50/30' : 'border-slate-300'
                        }`}
                      />
                      {errors.beskrivelse && (
                        <p className="text-red-500 text-xs mt-1 font-medium">{errors.beskrivelse}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-4 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-400 text-white font-extrabold transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent animate-spin" />
                          <span>Sender besked...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                          <span>Send henvendelse</span>
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
